import React, { useState, useEffect } from 'react';
import mfaService from '../../services/mfaService';

const MfaSetup = () => {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSetup, setActiveSetup] = useState(null); // 'authenticator', 'sms', 'hardware'
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  useEffect(() => {
    loadMfaData();
  }, []);

  const loadMfaData = async () => {
    try {
      setLoading(true);
      const [status, methodsList] = await Promise.all([
        mfaService.getMfaStatus(),
        mfaService.getMfaMethods()
      ]);
      setMfaStatus(status);
      setMethods(methodsList);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMethod = async (methodId) => {
    if (!window.confirm('Are you sure you want to remove this MFA method?')) {
      return;
    }

    try {
      await mfaService.removeMfaMethod(methodId);
      await loadMfaData();
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSetPrimary = async (methodId) => {
    try {
      await mfaService.setPrimaryMethod(methodId);
      await loadMfaData();
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGenerateRecoveryCodes = async () => {
    try {
      const result = await mfaService.generateRecoveryCodes(10);
      setRecoveryCodes(result.codes);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '24px' }}>
        Multi-Factor Authentication
      </h2>

      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {/* MFA Status */}
      <div style={{
        padding: '20px',
        backgroundColor: mfaStatus?.isEnabled ? '#d1fae5' : '#fef3c7',
        border: `1px solid ${mfaStatus?.isEnabled ? '#6ee7b7' : '#fbbf24'}`,
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
          MFA Status: {mfaStatus?.isEnabled ? 'Enabled' : 'Disabled'}
        </h3>
        {mfaStatus?.isRequired && (
          <p style={{ fontSize: '14px', color: '#92400e', margin: '4px 0' }}>
            MFA is required by your organization policy.
          </p>
        )}
        {mfaStatus?.shouldEnforce && !mfaStatus?.isEnabled && (
          <p style={{ fontSize: '14px', color: '#92400e', margin: '4px 0' }}>
            You must enable MFA to continue using your account.
          </p>
        )}
        <p style={{ fontSize: '14px', color: '#6b7280', margin: '8px 0 0 0' }}>
          Registered methods: {mfaStatus?.methodCount || 0}
        </p>
      </div>

      {/* Registered Methods */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
          Registered MFA Methods
        </h3>
        {methods.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            No MFA methods registered. Add one below to enable MFA.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {methods.map((method) => (
              <div
                key={method.id}
                style={{
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '16px' }}>
                    {method.methodType === 'AuthenticatorApp' && '🔐 Authenticator App'}
                    {method.methodType === 'SMS' && '📱 SMS'}
                    {method.methodType === 'FIDO2' && '🔑 Hardware Key'}
                    {method.isPrimary && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        color: '#059669',
                        fontWeight: 500
                      }}>
                        (Primary)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                    {method.deviceName}
                    {method.phoneNumber && ` • ${method.phoneNumber}`}
                  </div>
                  {method.lastUsedAt && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      Last used: {new Date(method.lastUsedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!method.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(method.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Set Primary
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveMethod(method.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#991b1b',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Method */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
          Add MFA Method
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveSetup('authenticator')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Add Authenticator App
          </button>
          <button
            onClick={() => setActiveSetup('sms')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Add SMS
          </button>
          <button
            onClick={() => setActiveSetup('hardware')}
            disabled
            style={{
              padding: '12px 24px',
              backgroundColor: '#9ca3af',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'not-allowed',
              opacity: 0.6
            }}
            title="Hardware key support coming soon"
          >
            Add Hardware Key (Coming Soon)
          </button>
        </div>
      </div>

      {/* Setup Forms */}
      {activeSetup === 'authenticator' && (
        <AuthenticatorSetupForm
          onComplete={() => {
            setActiveSetup(null);
            loadMfaData();
          }}
          onCancel={() => setActiveSetup(null)}
        />
      )}

      {activeSetup === 'sms' && (
        <SmsSetupForm
          onComplete={() => {
            setActiveSetup(null);
            loadMfaData();
          }}
          onCancel={() => setActiveSetup(null)}
        />
      )}

      {/* Recovery Codes */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
          Recovery Codes
        </h3>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
          Recovery codes can be used to access your account if you lose access to your MFA methods.
          Store them in a safe place.
        </p>
        {recoveryCodes ? (
          <div>
            <div style={{
              padding: '16px',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              marginBottom: '16px',
              fontFamily: 'monospace',
              fontSize: '14px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '8px'
            }}>
              {recoveryCodes.map((code, index) => (
                <div key={index}>{code}</div>
              ))}
            </div>
            <button
              onClick={() => {
                const text = recoveryCodes.join('\n');
                navigator.clipboard.writeText(text);
                alert('Recovery codes copied to clipboard');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Copy All Codes
            </button>
          </div>
        ) : (
          <button
            onClick={handleGenerateRecoveryCodes}
            style={{
              padding: '10px 20px',
              backgroundColor: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Generate Recovery Codes
          </button>
        )}
      </div>
    </div>
  );
};

// Authenticator App Setup Form
const AuthenticatorSetupForm = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1); // 1: setup, 2: verify
  const [deviceName, setDeviceName] = useState('');
  const [secret, setSecret] = useState(null);
  const [qrCodeUri, setQrCodeUri] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSetup = async () => {
    if (!deviceName.trim()) {
      setError('Device name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await mfaService.setupAuthenticatorApp(deviceName.trim());
      setSecret(result.secret);
      setQrCodeUri(result.qrCodeUri);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await mfaService.verifyAuthenticatorApp(secret, verificationCode.trim(), deviceName.trim());
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '24px',
      marginTop: '24px'
    }}>
      <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
        Setup Authenticator App
      </h4>

      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {step === 1 && (
        <div>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            Install an authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
            on your phone, then enter a name for this device.
          </p>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Device Name
            </label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="e.g., iPhone, Android Phone"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSetup}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: loading ? '#9ca3af' : '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Setting up...' : 'Continue'}
            </button>
            <button
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 2 && secret && qrCodeUri && (
        <div>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            Scan this QR code with your authenticator app, then enter the 6-digit code to verify.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <img 
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUri)}`}
              alt="QR Code"
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Manual Entry Key (if QR code doesn't work)
            </label>
            <input
              type="text"
              value={secret}
              readOnly
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'monospace',
                backgroundColor: '#f9fafb'
              }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Verification Code
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                textAlign: 'center',
                letterSpacing: '4px',
                fontFamily: 'monospace'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleVerify}
              disabled={loading || verificationCode.length !== 6}
              style={{
                padding: '10px 20px',
                backgroundColor: (loading || verificationCode.length !== 6) ? '#9ca3af' : '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: (loading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Verifying...' : 'Verify & Complete'}
            </button>
            <button
              onClick={() => setStep(1)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// SMS Setup Form
const SmsSetupForm = ({ onComplete, onCancel }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!phoneNumber.trim() || !deviceName.trim()) {
      setError('Phone number and device name are required');
      return;
    }

    // Basic phone validation
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(phoneNumber.replace(/\s/g, ''))) {
      setError('Please enter a valid phone number');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await mfaService.setupSms(phoneNumber.trim(), deviceName.trim());
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '24px',
      marginTop: '24px'
    }}>
      <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
        Setup SMS Authentication
      </h4>

      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
        Enter your phone number to receive verification codes via SMS.
      </p>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
          Phone Number
        </label>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="+1234567890"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
          Device Name
        </label>
        <input
          type="text"
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder="e.g., iPhone, Android Phone"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: loading ? '#9ca3af' : '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Setting up...' : 'Complete Setup'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default MfaSetup;

